/**
 * ConceptPathView — Timeline accordion del percorso di un concetto.
 *
 * Ogni tappa è un accordion collapsato (2 righe di preview). Click
 * apre il testo completo inline; click di nuovo chiude. L'autore di
 * provenienza (da cui si è entrati) è evidenziato visivamente.
 */

import { BaseComponent } from '@/core/BaseComponent';
import type { ComponentContext } from '@/core/BaseComponent';
import { getDataService } from '@/services/data-registry';
import type { ConceptLocalized, TappaLocalized } from '@/data/schema';

export class ConceptPathView extends BaseComponent {
  private readonly conceptId: string;
  private readonly sourceAuthorId: string | undefined;

  constructor(
    element: HTMLElement,
    context: ComponentContext,
    conceptId: string,
    sourceAuthorId?: string,
  ) {
    super(element, context);
    this.conceptId = conceptId;
    this.sourceAuthorId = sourceAuthorId;
  }

  protected async onInit(): Promise<void> {
    this.element.classList.add('concept-path-view');
    const data = getDataService();

    const [concept, tappe, allAuthors] = await Promise.all([
      data.getConcept(this.conceptId, this.context.locale),
      data.getTappeByConcept(this.conceptId, this.context.locale),
      data.listAuthors({}, this.context.locale),
    ]);

    if (!concept) {
      this.renderNotFound();
      return;
    }

    const authorMap = new Map(allAuthors.map((a) => [a.id, a]));
    this.render(concept, tappe, authorMap);
    this.wireEvents();

    // Auto-scroll to the source author's stop if present.
    if (this.sourceAuthorId) {
      requestAnimationFrame(() => {
        const el = this.element.querySelector<HTMLElement>(
          `[data-stop-author="${this.sourceAuthorId}"]`,
        );
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }

  private renderNotFound(): void {
    this.element.innerHTML = `
      <div class="empty-state">
        <p>Concetto non trovato: <code>${esc(this.conceptId)}</code></p>
        <button type="button" class="btn" data-action="home">Torna alla lista</button>
      </div>
    `;
    this.addEventListener(this.element, 'click', () => {
      this.context.bus.emit('navigate:home', {});
    });
  }

  private render(
    concept: ConceptLocalized,
    tappe: TappaLocalized[],
    authorMap: Map<string, { id: string; nome: string }>,
  ): void {
    const stopsHtml =
      tappe.length > 0
        ? tappe
            .map((t, i) => {
              const author = authorMap.get(t.autore_id);
              const authorName = author?.nome ?? t.autore_id;
              const anno = formatYear(t.anno_riferimento);
              const preview = t.content.spiegazione
                .slice(0, 100)
                .replace(/\s+\S*$/, '…');
              const isLast = i === tappe.length - 1;
              const isSource = t.autore_id === this.sourceAuthorId;

              const fullContent = `
                <div class="tappa-body" data-fragment-id="tappa:${attr(t.id)}/spiegazione">${paragraphs(t.content.spiegazione)}</div>
                ${this.renderCitazioni(t)}
                <p class="path-stop-deeplink"><button type="button" class="chip chip-link" data-tappa-link="${attr(t.id)}">Leggi tappa completa →</button></p>
              `;

              return `
                <li class="path-stop ${isLast ? 'path-stop-last' : ''} ${isSource ? 'path-stop-source' : ''}">
                  <div class="path-stop-marker">
                    <span class="path-stop-dot"></span>
                    ${!isLast ? '<span class="path-stop-line"></span>' : ''}
                  </div>
                  <div class="path-stop-card" data-stop-author="${attr(t.autore_id)}" data-stop-id="${attr(t.id)}">
                    <button type="button" class="path-stop-header" data-accordion-toggle="${attr(t.id)}">
                      <span class="path-stop-author">${esc(authorName)}${isSource ? ' <span class="path-stop-you">▸ sei qui</span>' : ''}</span>
                      <span class="path-stop-anno">${esc(anno)}</span>
                      <span class="path-stop-preview">${esc(preview)}</span>
                    </button>
                    <div class="path-stop-content" data-accordion-body="${attr(t.id)}" hidden>
                      ${fullContent}
                    </div>
                  </div>
                </li>
              `;
            })
            .join('')
        : `<li class="empty-state">Nessuna tappa ancora scritta per questo concetto.</li>`;

    this.element.innerHTML = `
      <button type="button" class="btn btn-back" data-action="back">← Indietro</button>

      <header class="concept-header">
        <h1 class="concept-title" data-fragment-id="concept:${attr(concept.id)}/titolo">${esc(concept.nome)}</h1>
        ${concept.content.descrizione ? `<div class="concept-description" data-fragment-id="concept:${attr(concept.id)}/descrizione">${paragraphs(concept.content.descrizione)}</div>` : ''}
      </header>

      <section class="concept-path-section">
        <h2>Il percorso dell'idea</h2>
        <ol class="path-timeline">${stopsHtml}</ol>
      </section>
    `;
  }

  private renderCitazioni(t: TappaLocalized): string {
    if (t.content.citazioni.length === 0) return '';
    const items = t.content.citazioni.map((c) => {
      const meta = t.citazioni.find((m) => m.id === c.id);
      const rif = meta?.riferimento;
      const rifLabel = rif
        ? [rif.titolo, rif.edizione, rif.passaggio].filter(Boolean).join(', ')
        : '';
      return `
        <figure class="tappa-citazione">
          <blockquote class="tappa-citazione-originale" lang="${attr(meta?.lingua_originale ?? 'la')}">${esc(c.testo_originale)}</blockquote>
          <blockquote class="tappa-citazione-tradotta">${esc(c.testo_tradotto)}</blockquote>
          ${rifLabel ? `<figcaption class="tappa-citazione-fonte">— ${esc(rifLabel)}</figcaption>` : ''}
        </figure>
      `;
    });
    return `<h3>Citazioni</h3>${items.join('')}`;
  }

  private wireEvents(): void {
    this.addEventListener(this.element, 'click', (ev) => {
      const t = ev.target as HTMLElement;

      // Back navigation → vista precedente (stack), non sempre l'autore/home.
      if (t.closest<HTMLElement>('[data-action="back"]')) {
        this.context.bus.emit('navigate:back', {});
        return;
      }
      if (t.dataset.action === 'home') {
        this.context.bus.emit('navigate:home', {});
        return;
      }

      // Deep link to full tappa
      const tappaLink = t.closest<HTMLElement>('[data-tappa-link]');
      if (tappaLink?.dataset.tappaLink) {
        this.context.bus.emit('navigate:tappa', { id: tappaLink.dataset.tappaLink });
        return;
      }

      // Accordion toggle
      const toggle = t.closest<HTMLElement>('[data-accordion-toggle]');
      if (toggle?.dataset.accordionToggle) {
        const id = toggle.dataset.accordionToggle;
        const body = this.element.querySelector<HTMLElement>(
          `[data-accordion-body="${id}"]`,
        );
        if (!body) return;
        const isOpen = !body.hidden;
        // Close all others first
        this.element.querySelectorAll<HTMLElement>('[data-accordion-body]').forEach((b) => {
          b.hidden = true;
          b.closest('.path-stop-card')?.classList.remove('is-open');
        });
        if (!isOpen) {
          body.hidden = false;
          body.closest('.path-stop-card')?.classList.add('is-open');
        }
      }
    });
  }
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function attr(s: string): string {
  return esc(s).replace(/"/g, '&quot;');
}

function formatYear(y: number): string {
  if (y < 0) return `${Math.abs(y)} a.C.`;
  return `${y} d.C.`;
}

function paragraphs(text: string): string {
  return text
    .split(/\n\n+/)
    .map((p) => `<p>${esc(p.trim())}</p>`)
    .join('');
}
